import type { SourceStandardStatus } from "./kdStandardTypes";

export type SourceRole = "rule" | "reference" | "recommendation" | "example";
export interface SourceMetadata {
  title: string; description: string; purpose: string; scope: string; role: SourceRole;
}
export interface ReviewSource extends SourceMetadata {
  id: string; source_id: string; revision: number;
  status: "draft" | "active" | "retired";
  use_by_default?: boolean;
  processing_status: "queued" | "running" | "ready" | "failed";
  files: { id: string; filename: string; size_bytes: number; sha256: string }[];
  segments: { file_id: string; locator: string; text: string; heading?: string; image_ids?: string[] }[];
  images?: SourceImage[];
  parser_version?: string;
  rules_config?: SourceRulePlan;
  tables?: (PipeTable | TransitionTable)[];
  standard_facts?: StandardFact[];
  table_count?: number;
  standard_statuses?: SourceStandardStatus[];
  selectable_for_new_checks?: boolean;
  warnings: string[]; error_message: string | null; created_at: string; confirmed_at: string | null;
}
export interface SourceFragment {
  version_id: string; title: string; revision: number; status: "draft" | "active" | "retired";
  locator: string; reference: string; total_matches: number; limited: boolean;
  matches: { file: ReviewSource["files"][number]; segments: {
    segment_index: number; locator: string; text: string; heading: string; is_match: boolean;
  }[] }[];
}
export interface PipeTable {
  id: string; file_id: string; standard: string; caption: string; locator: string;
  thicknesses: string[]; rows: { diameter: string; values: string[]; borders: string[]; segment_index: number }[];
  header_indices: number[]; note_indices: number[]; issues: string[]; policy: "gost-8732-table-1";
}
export interface PipeRangeReport {
  status: string; trial: boolean; counts: Record<string, number>; limitations: string[];
  entries: { item: { raw_text: string; position: string; bbox: [number, number, number, number] };
    document_id: string; page: number; status: string; reason: string; trial: boolean;
    finding_id?: string; citations: SourceCitation[]; notes?: { locator: string; text: string }[] }[];
}
export interface SourceImage { id: string; file_id: string; ordinal: number; locator: string; sha256: string; width: number; height: number }
export interface InstructionRule {
  id: string; title: string; statement: string; applies_to: string; exceptions: string;
  check_kind?: "ai" | "drawing_litera" | "single_sheet_number";
  target: "item" | "page"; segment_indices: number[]; image_ids: string[];
}
export interface SourceDisposition { segment_index: number; kind: "rule" | "context" | "open_question" | "unclassified"; reason: string }
export interface SourceRulePlan { revision?: number; rules?: InstructionRule[]; dispositions?: SourceDisposition[]; reviewed_at?: string | null; comment?: string; sha256?: string }
export interface SourceRuleUpdate { expected_revision: number; rules: InstructionRule[]; dispositions: SourceDisposition[]; reviewed: boolean; comment: string }
export interface SourceCitation {
  citation_id: string; version_id: string; title: string; revision: number;
  role: string; file_id: string; filename: string; locator: string; excerpt: string;
  image_id?: string | null;
}

export interface InstructionReport {
  trial?: boolean;
  status: "complete" | "partial"; preliminary: boolean; rules_total: number; items_total: number;
  items_complete: boolean; pairs_total: number; omitted_pairs: number; counts: Record<string, number>; limitations: string[];
  citations?: Record<string, SourceCitation>;
  coverage: { version_id: string; title: string; revision: number; total_segments: number; included_indices: number[]; omitted_indices: number[]; images_total: number; included_image_ids: string[]; omitted_image_ids: string[]; rules_reviewed: boolean; rules_total: number; omitted_rule_ids: string[]; trial?: boolean; unclassified_indices?: number[] }[];
  entries: { rule_key: string; rule_title: string; source_title: string; version_id: string; revision: number; role: string; trial?: boolean;
    applies_to: string; exceptions: string; item: { id: string; raw_text: string; position: string; section: string; bbox: [number, number, number, number] };
    status: "matched" | "deviation" | "not_applicable" | "needs_review" | "not_checked";
    reason: string; observed: string; expected: string; citation_ids?: string[]; citations?: SourceCitation[]; document_id: string; page: number; finding_id?: string }[];
}
export const sourceRoleLabels: Record<SourceRole, string> = {
  rule: "Внутреннее правило", reference: "Справочник", recommendation: "Рекомендация", example: "Пример с замечаниями",
};

export interface TransitionTable {
  id: string; file_id: string; standard: string; caption: string; locator: string;
  execution: "1" | "2"; policy: "gost-17378-tables-1-2";
  rows: { dn: string; diameter: string; wall: string; small_diameter: string; small_wall: string;
    length: string; mass: string; needs_review: boolean; segment_indices: number[]; locator: string }[];
  header_indices: number[]; note_indices: number[]; issues: string[];
}
export interface StandardFact {
  id: string; file_id: string; standard: string; kind: "material_scope" | "technical_conditions";
  value: string; segment_indices: number[];
}
